/**
 * The client half of the custom question contract.
 *
 * App\Services\CustomQuestions\CustomQuestionApi is the other half and the one
 *  that documents it; CustomQuestionApiTest keeps the two and
 *  docs/custom-questions.md from drifting apart.
 */

export const CUSTOM_QUESTION_API_VERSION = 1;

/**
 * Which situation a question is being rendered in. A question gets this as
 *  breely.mode so one document can be a control while filling and a summary
 *  when an admin reads the submission.
 */
export const CustomQuestionMode = {
    FILL: 'fill',
    READONLY: 'readonly',
    ANSWER: 'answer',
    PREVIEW: 'preview',
} as const;

export type CustomQuestionModeName =
    (typeof CustomQuestionMode)[keyof typeof CustomQuestionMode];

/**
 * Messages the frame sends up. Anything else is ignored.
 */
export const FrameMessage = {
    READY: 'ready',
    VALUE: 'value',
    VALID: 'valid',
    CHARGES: 'charges',
    HEIGHT: 'height',
    ERROR: 'error',
    /** Requests for the host to act, each answered with HostMessage.RESULT */
    CANCEL_EVENT: 'cancelEvent',
    GET_CLIENT_HISTORY: 'getClientHistory',
} as const;

/**
 * Messages the host sends down.
 */
export const HostMessage = {
    INIT: 'init',
    VALUE: 'value',
    SETTINGS: 'settings',
    CONFIRMATION: 'confirmation',
    /** The answer to a request the frame made, matched on its requestId */
    RESULT: 'result',
    /** The client logged in or out */
    CLIENT_CHANGED: 'clientChanged',
} as const;

/**
 * What a request to the host - breely.cancelEvent, breely.getClientHistory -
 *  rejects with when it isn't the server saying no. Mirrors
 *  App\Services\CustomQuestions\CustomQuestionApi::requestErrors().
 */
export const RequestError = {
    NOT_AVAILABLE: 'not-available',
    NOT_FOUND: 'not-found',
    CANNOT_CANCEL: 'cannot-cancel',
} as const;

export type CustomQuestionConfirmationEvent = {
    id: number;
    name: string | null;
    category: string | null;
    description: string | null;
    start: string | null;
    end: string | null;
    timezone: string | null;
    isAllDay: boolean;
    isSeries: boolean;
    location: string | null;
    resource: { id: number; name: string } | null;
    price: number;
    amountPaid: number;
    amountOwed: number;
    canceledAt: string | null;
    canCancel: boolean;
    canReschedule: boolean;
};

/**
 * breely.confirmation - the submission a confirmation section is confirming.
 *  Built on the server by CustomQuestionConfirmation, and documented field by
 *  field in CustomQuestionApi::confirmation().
 */
export type CustomQuestionConfirmation = {
    submission: {
        id: number;
        formId: number;
        formName: string | null;
        submittedAt: string | null;
    };
    client: {
        firstName: string | null;
        lastName: string | null;
        email: string | null;
        phone: string | null;
    } | null;
    answers: Array<{
        questionId: number;
        label: string | null;
        type: string;
        value: any;
        text: string;
    }>;
    events: CustomQuestionConfirmationEvent[];
    packages: Array<{
        id: number;
        name: string | null;
        remainingBalance: any;
    }>;
    giftCertificates: Array<{
        id: number;
        value: number;
        remainingValue: number;
    }>;
};

export type CustomQuestionField = {
    key: string;
    type: string;
    label?: string;
    description?: string;
    default?: any;
    options?: Array<{ value: string; label: string }>;
    min?: number;
    max?: number;
    step?: number;
    maxLength?: number;
    text?: string;
};

/**
 * The kinds of section a form is made of. A custom question says which of
 *  these it belongs in, and the Add Element panel only offers it there.
 *  Mirrors App\Services\CustomQuestions\CustomQuestionApi::sectionTypes().
 */
export const SectionType = {
    FORM: 'form',
    THANK_YOU: 'thankyou',
    PAYMENT: 'payment',
    INVOICE: 'invoice',
} as const;

export type SectionTypeName = (typeof SectionType)[keyof typeof SectionType];

/** What each one is called in the editor */
export const SECTION_TYPE_LABELS: Record<SectionTypeName, string> = {
    form: 'Form questions',
    thankyou: 'Confirmation (thank you)',
    payment: 'Payment step',
    invoice: 'Invoices',
};

export const DEFAULT_SECTION_TYPES: SectionTypeName[] = [SectionType.FORM];

/**
 * Which kind of section this is, matching FormSection::sectionType() on the
 *  server - including the order, since a section could carry more than one
 *  of these settings.
 */
export function sectionTypeOf(section?: {
    settings?: Record<string, any>;
} | null): SectionTypeName {
    const settings = section?.settings ?? {};

    if (settings.thankyou) {
        return SectionType.THANK_YOU;
    }

    if (settings.payment) {
        return SectionType.PAYMENT;
    }

    if (settings.invoice) {
        return SectionType.INVOICE;
    }

    return SectionType.FORM;
}

export type CustomQuestionFlags = {
    noLabel?: boolean;
    noRequired?: boolean;
    alwaysRequired?: boolean;
    excludeFromAnswers?: boolean;
    allowPrefill?: boolean;
    oncePerTransaction?: boolean;
    rendersAnswer?: boolean;
    minHeight?: number;
    maxHeight?: number;
    sections?: SectionTypeName[];
};

export type CustomQuestionDefinition = {
    id: number;
    name: string;
    icon?: string;
    category?: string;
    version?: number;
    html: string;
    css: string;
    js: string;
    fields: CustomQuestionField[];
    answerSchema?: {
        type?: string;
        summary?: string;
        fields?: Array<{ key: string; label?: string; type?: string }>;
    };
    flags?: CustomQuestionFlags;
};

/**
 * How long the frame has to call breely.ready() before we assume its code is
 *  broken or whatever it loads is never arriving.
 */
export const READY_TIMEOUT_MS = 8000;

export const DEFAULT_MIN_HEIGHT = 48;

export const MAX_FRAME_HEIGHT = 4000;

/**
 * Resolve the settings a question instance has against the fields it declared,
 *  filling in defaults. The server does the same thing in
 *  App\Services\CustomQuestions\SettingsSchema::resolve for the paths that
 *  never reach a browser.
 */
export function resolveSettings(
    fields: CustomQuestionField[] = [],
    settings: Record<string, any> = {},
): Record<string, any> {
    const resolved: Record<string, any> = {};

    for (const field of fields) {
        if (field.type === 'note' || !field.key) {
            continue;
        }

        const value = settings?.[field.key];

        if (value === undefined || value === null || value === '') {
            resolved[field.key] = field.default ?? null;
            continue;
        }

        switch (field.type) {
            case 'number':
                resolved[field.key] = isNaN(parseFloat(value))
                    ? (field.default ?? null)
                    : parseFloat(value);
                break;
            case 'toggle':
                resolved[field.key] = !!value;
                break;
            case 'list':
                resolved[field.key] = Array.isArray(value) ? value : [value];
                break;
            default:
                resolved[field.key] = value;
        }
    }

    return resolved;
}

/**
 * Every priced entry a question instance has, which is the only thing
 *  breely.setCharges may name. Prices here are for display - the server looks
 *  them up again from the same settings when it charges for the answer.
 */
export function priceListFor(
    fields: CustomQuestionField[] = [],
    settings: Record<string, any> = {},
): Array<Record<string, any>> {
    const resolved = resolveSettings(fields, settings);

    return fields
        .filter(field => field.type === 'priceList')
        .flatMap(field => resolved[field.key] || []);
}

/**
 * What breely.getClientHistory() resolves with. Built on the server by
 *  CustomQuestionClientHistory, and documented field by field in
 *  CustomQuestionApi::clientHistory().
 */
export type CustomQuestionClientHistory = {
    client: CustomQuestionConfirmation['client'];
    events: CustomQuestionConfirmationEvent[];
    packages: Array<{
        id: number;
        name: string | null;
        isSubscription: boolean;
        status: string;
        remainingBalance: unknown;
        expiresAt: string | null;
        nextRenewalAt: string | null;
    }>;
    invoices: Array<{
        id: number;
        number: number | null;
        status: string;
        total: number;
        amountPaid: number;
        sentAt: string | null;
        dueAt: string | null;
    }>;
    waitlist: Array<{
        id: number;
        name: string | null;
        start: string | null;
        end: string | null;
    }>;
};
